import { StateMachine } from "@/core/arch/state_machine";
import type { AdminsRepository } from "./admins_repository";
import type {
  AdminsListState,
  CreateAdminInput,
  SyncGoogleIdentitiesResult,
} from "./types";

const initialState: AdminsListState = {
  status: "idle",
  items: [],
  error: null,
  pendingAction: null,
};

export class AdminsListBloc extends StateMachine<AdminsListState> {
  constructor(private repo: AdminsRepository) {
    super(initialState);
  }

  async load(): Promise<void> {
    this.setState({ ...this.state, status: "loading", error: null });
    try {
      const items = await this.repo.list();
      this.setState({
        ...this.state,
        status: "loaded",
        items,
        pendingAction: null,
      });
    } catch (error) {
      this.setState({
        ...this.state,
        status: "error",
        error: error instanceof Error ? error.message : "Failed to load admins",
        pendingAction: null,
      });
    }
  }

  async createAdmin(input: CreateAdminInput): Promise<{
    success: boolean;
    mode: "created" | "promoted" | "linked" | "";
    errorCode: string | null;
    errors: string[];
  }> {
    this.setState({ ...this.state, pendingAction: "create" });
    try {
      const result = await this.repo.create(input);
      if (result.success) {
        await this.load();
      } else {
        this.setState({ ...this.state, pendingAction: null });
      }
      return result;
    } catch (error) {
      this.setState({ ...this.state, pendingAction: null });
      return {
        success: false,
        mode: "",
        errorCode: null,
        errors: [
          error instanceof Error ? error.message : "Failed to create admin",
        ],
      };
    }
  }

  async syncGoogleIdentities(): Promise<SyncGoogleIdentitiesResult> {
    this.setState({ ...this.state, pendingAction: "sync" });
    try {
      const result = await this.repo.syncGoogleIdentities();
      if (result.linkedEmails.length > 0) {
        await this.load();
      } else {
        this.setState({ ...this.state, pendingAction: null });
      }
      return result;
    } catch (error) {
      this.setState({ ...this.state, pendingAction: null });
      return {
        success: false,
        linkedEmails: [],
        skippedCount: 0,
        errors: [
          error instanceof Error
            ? error.message
            : "Failed to sync Google logins",
        ],
      };
    }
  }

  async deactivateAdmin(
    sub: string,
  ): Promise<{ success: boolean; errors: string[] }> {
    this.setState({ ...this.state, pendingAction: "deactivate" });
    try {
      const result = await this.repo.deactivate(sub);
      if (result.success) {
        await this.load();
      } else {
        this.setState({ ...this.state, pendingAction: null });
      }
      return result;
    } catch (error) {
      this.setState({ ...this.state, pendingAction: null });
      return {
        success: false,
        errors: [
          error instanceof Error ? error.message : "Failed to deactivate admin",
        ],
      };
    }
  }
}
